import { Box, Card, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useTheme } from "@mui/material"
import SubtaskIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined"
import { PRIORITY_COLORS, STATUS_FALLBACK_COLORS } from "../../theme"
import { formatDate } from "../../utils/format"
import { rise } from "../../motion"
import { filledChipSx, outlinedChipSx } from "../../theme/contrast"

export default function TaskList({ tasks, onOpen }) {
  // The surface decides which way a data colour has to move to become legible.
  const theme = useTheme()
  return (
    <Card>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Due</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task, index) => (
              <TableRow key={task.id} hover sx={{ cursor: "pointer", ...rise(index) }} onClick={() => onOpen(task.id)}>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                    {task.task_code}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {/* A step inside something, said on the row. Sub-tasks are
                        not filtered out of this list — one assigned to you is
                        yours, and hiding it would lose it — so the row has to
                        say that it is part of something larger. */}
                    {task.parent_task_id ? (
                      <Tooltip title={task.parent_title ? `A step inside “${task.parent_title}”` : "A step inside another task"}>
                        <SubtaskIcon sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0 }} />
                      </Tooltip>
                    ) : null}
                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                      {task.title}
                    </Typography>
                    {task.subtask_total > 0 ? (
                      <Chip
                        size="small"
                        label={`${task.subtask_done}/${task.subtask_total}`}
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={task.status_label}
                    sx={{
                      height: 22,
                      fontWeight: 600,
                      /* The ink is measured, not assumed. These colours come from
                         the database and one of the seeded seven — indigo "To Do"
                         — put near-black at 2.7:1, which is not text. */
                      ...filledChipSx(task.status_color || STATUS_FALLBACK_COLORS[task.status_name]),
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={task.priority}
                    /* Darkened for the surface it sits on. Amber at 11px was
                       2.18:1 against white — a word, in the colour of a word,
                       that could not be read. */
                    sx={{ height: 22, ...outlinedChipSx(PRIORITY_COLORS[task.priority], theme.palette.background.paper) }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {task.assignee_name || "Unassigned"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {task.project_code || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color={task.due_date && new Date(task.due_date) < new Date() && !task.is_terminal ? "error.main" : "text.secondary"}>
                    {formatDate(task.due_date)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Card>
  )
}
